/**
 * Lift2Fit — the in-browser fallback for the lifter's own programme and grid.
 *
 * `lib/api.ts` reaches for this whenever `GET /programme`, `/sessions/active` or
 * a programme mutation cannot reach the API, exactly like the dashboard falls
 * back to `demoData`. It is a working, in-memory implementation of the 0007
 * contract — not a frozen fixture — so the builder, the day editor and the live
 * grid can all be driven with no backend, and a reviewer sees real behaviour.
 *
 * A brand-new demo account (`u-demo-new`) gets nothing: no plan, no rows and no
 * in-progress session, so the honest empty states are what render.
 */
import type {
  ActiveSessionPayload,
  AddSessionExerciseInput,
  CompleteSessionResponse,
  ExerciseGroupType,
  LogSessionSetInput,
  PersonalRecordWire,
  PlanHistoryEntry,
  ProgrammeDayPatch,
  ProgrammeDaySummary,
  ProgrammeDraft,
  ProgrammeExerciseInput,
  ProgrammeExercisePatch,
  ProgrammeMutationResult,
  ProgrammeOverview,
  ProgrammePlan,
  ProgrammePlanPatch,
  ProgrammeRow,
  ProgrammeWarmup,
  SessionExercise,
  SessionSet,
  SessionSetResponse,
  SessionTotalsWire,
  SetMeasure,
  StartSessionInput,
  WorkoutSessionWire,
} from '../lib/api';
import { isFreshDemoAccount, trackMeta } from './demoData';

export interface DemoDayInput {
  label: string;
  focus?: string[];
  index?: number;
}

const NOW = (): string => new Date().toISOString();

function uuid(prefix: string, index: number): string {
  const tail = String(index).padStart(12, '0');
  return `${prefix}0000-0000-4000-8000-${tail}`;
}

interface ExerciseSeed {
  exerciseId: string;
  movement: string;
  variant: string | null;
  equipment: string;
  muscleGroup: string;
  sets: number;
  repMin: number;
  repMax: number;
  targetRpe: number;
  targetWeightKg: number | null;
  restSeconds: number;
  perSide?: boolean;
  tempo?: string | null;
  notes?: string | null;
}

interface DaySeed {
  dayIndex: number;
  label: string;
  focus: string[];
  exercises: ExerciseSeed[];
}

/**
 * The demo persona's block: four days, nine prescribed movements, matching the
 * seeded real account so the two paths read the same.
 */
const STRENGTH_BLOCK: DaySeed[] = [
  {
    dayIndex: 1,
    label: 'Upper A',
    focus: ['chest', 'back', 'shoulders'],
    exercises: [
      { exerciseId: 'bench-press', movement: 'Bench Press', variant: 'Barbell', equipment: 'barbell', muscleGroup: 'chest', sets: 4, repMin: 3, repMax: 6, targetRpe: 7.5, targetWeightKg: 65, restSeconds: 210, notes: 'Shoulder blades pinned; bar to the lower sternum.' },
      { exerciseId: 'barbell-row', movement: 'Row', variant: 'Single Arm, Knee Supported', equipment: 'dumbbell', muscleGroup: 'back', sets: 4, repMin: 3, repMax: 6, targetRpe: 7.5, targetWeightKg: 32, restSeconds: 210, perSide: true },
      { exerciseId: 'overhead-press', movement: 'Shoulder Press', variant: 'Half Kneeling', equipment: 'dumbbell', muscleGroup: 'shoulders', sets: 3, repMin: 5, repMax: 8, targetRpe: 7.5, targetWeightKg: 22, restSeconds: 150, perSide: true },
    ],
  },
  {
    dayIndex: 2,
    label: 'Lower A',
    focus: ['quads', 'posterior'],
    exercises: [
      { exerciseId: 'back-squat', movement: 'Squat', variant: 'High Bar', equipment: 'barbell', muscleGroup: 'quads', sets: 4, repMin: 3, repMax: 6, targetRpe: 7.5, targetWeightKg: 100, restSeconds: 240 },
      { exerciseId: 'romanian-deadlift', movement: 'Romanian Deadlift', variant: null, equipment: 'barbell', muscleGroup: 'hamstrings', sets: 3, repMin: 6, repMax: 10, targetRpe: 7, targetWeightKg: 80, restSeconds: 150 },
    ],
  },
  {
    dayIndex: 3,
    label: 'Upper B',
    focus: ['back', 'press'],
    exercises: [
      { exerciseId: 'weighted-pull-up', movement: 'Pull-up', variant: 'Weighted', equipment: 'bodyweight', muscleGroup: 'back', sets: 4, repMin: 4, repMax: 8, targetRpe: 8, targetWeightKg: 10, restSeconds: 150 },
      { exerciseId: 'incline-dumbbell-press', movement: 'Incline Press', variant: '30°', equipment: 'dumbbell', muscleGroup: 'chest', sets: 3, repMin: 6, repMax: 10, targetRpe: 7.5, targetWeightKg: 26, restSeconds: 120 },
    ],
  },
  {
    dayIndex: 4,
    label: 'Lower B',
    focus: ['hinge', 'single leg'],
    exercises: [
      { exerciseId: 'trap-bar-deadlift', movement: 'Trap Bar Deadlift', variant: null, equipment: 'barbell', muscleGroup: 'posterior', sets: 4, repMin: 3, repMax: 5, targetRpe: 8, targetWeightKg: 130, restSeconds: 240 },
      { exerciseId: 'front-squat', movement: 'Front Squat', variant: null, equipment: 'barbell', muscleGroup: 'quads', sets: 3, repMin: 5, repMax: 8, targetRpe: 7, targetWeightKg: 70, restSeconds: 180 },
    ],
  },
];

const DEMO_PLAN_ID = uuid('a1', 1);

interface EmptyDay {
  dayId: string;
  dayIndex: number;
  label: string;
  focus: string[];
}

interface DemoStore {
  planId: string;
  /** false for a brand-new account: the screen must show the empty state */
  hasPlan: boolean;
  plan: ProgrammePlan;
  rows: ProgrammeRow[];
  /** days that exist but have no prescribed exercise yet */
  emptyDays: EmptyDay[];
  /** the block's one warm-up session (0019), or null */
  warmup: ProgrammeWarmup | null;
  history: PlanHistoryEntry[];
  session: WorkoutSessionWire | null;
  exercises: SessionExercise[];
  sets: SessionSet[];
  seq: number;
}

let store: DemoStore | null = null;

function rowFromSeed(day: DaySeed, dayId: string, exercise: ExerciseSeed, orderIndex: number, index: number): ProgrammeRow {
  return {
    planId: DEMO_PLAN_ID,
    planName: 'Strength · Upper A / Lower A / Upper B / Lower B',
    planGoal: 'strength',
    track: 'strength',
    dayId,
    dayIndex: day.dayIndex,
    dayLabel: day.label,
    dayFocus: day.focus,
    planExerciseId: uuid('e1', index + 1),
    orderIndex,
    supersetGroup: null,
    groupType: null,
    exerciseId: exercise.exerciseId,
    exerciseName: exercise.variant ? `${exercise.movement}, ${exercise.variant}` : exercise.movement,
    movement: exercise.movement,
    variant: exercise.variant,
    equipment: exercise.equipment,
    muscleGroup: exercise.muscleGroup,
    prescribedSets: exercise.sets,
    repMin: exercise.repMin,
    repMax: exercise.repMax,
    measure: 'reps',
    perSide: exercise.perSide === true,
    isBodyweight: exercise.equipment === 'bodyweight',
    tempo: exercise.tempo ?? null,
    targetRpe: exercise.targetRpe,
    percent1rm: null,
    targetWeightKg: exercise.targetWeightKg,
    targetDistanceM: null,
    targetDurationS: null,
    restSeconds: exercise.restSeconds,
    progressionRule: 'double_progression',
    notes: exercise.notes ?? null,
  };
}

function emptyStore(): DemoStore {
  return {
    planId: DEMO_PLAN_ID,
    hasPlan: false,
    plan: {
      planId: DEMO_PLAN_ID,
      name: 'My training week',
      goal: 'strength',
      track: 'strength',
      trackLabel: trackMeta('strength').label,
      metricsFocus: ['e1rm', 'topSetLoad', 'tonnage'],
      weeks: 4,
      daysPerWeek: 0,
      source: 'lifter',
      isActive: true,
      deloadWeek: 4,
      weekPlan: [],
    },
    rows: [],
    emptyDays: [],
    warmup: null,
    history: [],
    session: null,
    exercises: [],
    sets: [],
    seq: 0,
  };
}

function seedStore(): DemoStore {
  if (isFreshDemoAccount()) return emptyStore();
  const rows: ProgrammeRow[] = [];
  let exerciseIndex = 0;
  for (const day of STRENGTH_BLOCK) {
    const dayId = uuid('d1', day.dayIndex);
    day.exercises.forEach((exercise, order) => {
      rows.push(rowFromSeed(day, dayId, exercise, order + 1, exerciseIndex));
      exerciseIndex += 1;
    });
  }
  const weeks = 8;
  return {
    planId: DEMO_PLAN_ID,
    hasPlan: true,
    plan: {
      planId: DEMO_PLAN_ID,
      name: 'Strength · Upper A / Lower A / Upper B / Lower B',
      goal: 'strength',
      track: 'strength',
      trackLabel: trackMeta('strength').label,
      metricsFocus: ['e1rm', 'topSetLoad', 'tonnage'],
      weeks,
      daysPerWeek: STRENGTH_BLOCK.length,
      source: 'coach',
      isActive: true,
      deloadWeek: 4,
      weekPlan: Array.from({ length: weeks }, (_, index) => ({ week: index + 1, isDeload: index + 1 === 4 || index + 1 === 8 })),
    },
    rows,
    emptyDays: [],
    warmup: null,
    history: [
      {
        planId: uuid('a2', 1),
        name: 'Hypertrophy · 3-day base',
        track: 'hypertrophy',
        trackLabel: trackMeta('hypertrophy').label,
        weeks: 6,
        daysPerWeek: 3,
        isActive: false,
        coachOwned: false,
        createdAt: '2026-03-02T09:00:00.000Z',
      },
    ],
    session: null,
    exercises: [],
    sets: [],
    seq: 0,
  };
}

function getStore(): DemoStore {
  if (!store) store = seedStore();
  return store;
}

/** Test/demo helper: forget any in-browser programme state. */
export function resetDemoProgramme(): void {
  store = null;
}

function overview(): ProgrammeOverview {
  const current = getStore();
  if (!current.hasPlan) return { plan: null, rows: [], days: [] };
  // Mirror the live envelope: `days` includes days whose only state is a label
  // (no prescription yet), so the builder can render and fill them one at a time.
  const days = new Map<string, ProgrammeDaySummary>();
  for (const day of current.emptyDays) {
    days.set(day.dayId, { id: day.dayId, dayIndex: day.dayIndex, label: day.label, focus: day.focus, exerciseCount: 0 });
  }
  for (const row of current.rows) {
    const existing = days.get(row.dayId);
    if (existing) existing.exerciseCount += 1;
    else
      days.set(row.dayId, {
        id: row.dayId,
        dayIndex: row.dayIndex,
        label: row.dayLabel,
        focus: row.dayFocus,
        exerciseCount: 1,
      });
  }
  return {
    plan: { ...current.plan, daysPerWeek: new Set(current.rows.map((row) => row.dayId)).size || current.plan.daysPerWeek },
    rows: current.rows.map((row) => ({ ...row })),
    days: [...days.values()].sort((a, b) => a.dayIndex - b.dayIndex),
    warmup: current.warmup ? { ...current.warmup, rows: current.warmup.rows.map((row) => ({ ...row })) } : null,
  };
}

function planHeader(planId: string | null | undefined): ProgrammeOverview {
  const current = getStore();
  if (!planId || planId === current.plan.planId) return overview();
  const entry = current.history.find((candidate) => candidate.planId === planId);
  if (!entry) return { plan: null, rows: [], days: [], warmup: null };
  return {
    plan: {
      planId: entry.planId,
      name: entry.name,
      goal: null,
      track: entry.track,
      trackLabel: entry.trackLabel,
      metricsFocus: [],
      weeks: entry.weeks,
      daysPerWeek: entry.daysPerWeek,
      source: 'lifter',
      isActive: entry.isActive,
      coachOwned: false,
      deloadWeek: null,
      weekPlan: [],
    },
    rows: [],
    days: [],
    warmup: null,
  };
}

export function demoProgrammeOverview(planId?: string | null): ProgrammeOverview {
  return planHeader(planId);
}

export function demoProgrammePlans(): PlanHistoryEntry[] {
  const current = getStore();
  if (!current.hasPlan) return [];
  return [
    {
      planId: current.plan.planId,
      name: current.plan.name,
      track: current.plan.track,
      trackLabel: current.plan.trackLabel,
      weeks: current.plan.weeks,
      daysPerWeek: new Set(current.rows.map((row) => row.dayId)).size,
      isActive: current.plan.isActive,
      coachOwned: current.plan.coachOwned === true,
      createdAt: null,
    },
    ...current.history.map((entry) => ({ ...entry })),
  ];
}

function nextId(prefix: string): string {
  const current = getStore();
  current.seq += 1;
  return uuid(prefix, current.seq + 100);
}

function rowFromInput(
  dayId: string,
  day: { dayIndex: number; label: string; focus: string[] },
  input: ProgrammeExerciseInput,
  orderIndex: number,
): ProgrammeRow {
  const current = getStore();
  return {
    planId: current.plan.planId,
    planName: current.plan.name,
    planGoal: current.plan.goal,
    track: current.plan.track,
    dayId,
    dayIndex: day.dayIndex,
    dayLabel: day.label,
    dayFocus: day.focus,
    planExerciseId: nextId('e1'),
    orderIndex,
    supersetGroup: input.supersetGroup ?? null,
    groupType: input.supersetGroup == null ? null : (input.groupType ?? 'superset'),
    exerciseId: input.exerciseId,
    exerciseName: input.exerciseId,
    movement: input.exerciseId,
    variant: null,
    equipment: 'barbell',
    muscleGroup: '',
    prescribedSets: input.sets,
    repMin: input.repMin,
    repMax: input.repMax,
    measure: input.measure ?? 'reps',
    perSide: input.perSide === true,
    isBodyweight: false,
    tempo: input.tempo ?? null,
    targetRpe: input.targetRpe,
    percent1rm: null,
    targetWeightKg: input.targetWeightKg ?? null,
    targetDistanceM: null,
    targetDurationS: null,
    restSeconds: input.restSeconds ?? 120,
    progressionRule: 'double_progression',
    notes: input.notes ?? null,
  };
}

export function demoCreateProgramme(draft: ProgrammeDraft): ProgrammeOverview {
  const current = getStore();
  const activate = draft.activate !== false;
  const planId = nextId('a1');
  const rows: ProgrammeRow[] = [];
  const emptyDays: EmptyDay[] = [];
  draft.days.forEach((day, index) => {
    const meta = { dayIndex: index + 1, label: day.label, focus: day.focus ?? [] };
    const dayId = nextId('d1');
    if (!day.exercises.length) {
      emptyDays.push({ dayId, ...meta });
      return;
    }
    for (const [order, exercise] of day.exercises.entries()) {
      rows.push(rowFromInput(dayId, meta, exercise, order + 1));
    }
  });

  // The plan this account was on moves to history unless it is made inactive
  // and the new one is not active.
  if (current.rows.length || current.emptyDays.length) {
    current.history = [
      {
        planId: current.plan.planId,
        name: current.plan.name,
        track: current.plan.track,
        trackLabel: current.plan.trackLabel,
        weeks: current.plan.weeks,
        daysPerWeek: current.plan.daysPerWeek,
        isActive: activate ? false : current.plan.isActive,
        coachOwned: current.plan.coachOwned === true,
        createdAt: null,
      },
      ...current.history.filter((entry) => entry.planId !== current.plan.planId),
    ];
  }

  const track = draft.track ?? current.plan.track;
  const weeks = draft.weeks;
  const deloadWeek = draft.deloadWeek ?? null;
  current.planId = planId;
  current.hasPlan = true;
  current.rows = rows;
  current.emptyDays = emptyDays;
  current.plan = {
    planId,
    name: draft.name,
    goal: current.plan.goal,
    track,
    trackLabel: trackMeta(track).label,
    metricsFocus: [],
    weeks,
    daysPerWeek: draft.days.length,
    source: 'lifter',
    isActive: activate,
    deloadWeek,
    weekPlan: Array.from({ length: weeks }, (_, index) => ({ week: index + 1, isDeload: index + 1 === deloadWeek })),
  };
  current.exercises = [];
  current.session = null;
  current.sets = [];
  return overview();
}

export function demoPatchProgramme(planId: string, patch: ProgrammePlanPatch): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  const weeks = patch.weeks ?? current.plan.weeks;
  const deloadWeek = patch.deloadWeek === undefined ? current.plan.deloadWeek : patch.deloadWeek;
  current.plan = {
    ...current.plan,
    name: patch.name ?? current.plan.name,
    weeks,
    deloadWeek,
    isActive: patch.isActive ?? current.plan.isActive,
    weekPlan: Array.from({ length: weeks }, (_, index) => ({ week: index + 1, isDeload: index + 1 === deloadWeek })),
  };
  return overview();
}

export function demoAddProgrammeDay(planId: string, input: DemoDayInput): ProgrammeMutationResult {
  const current = getStore();
  if (planId !== current.plan.planId) return { overview: overview(), dayId: null };
  const dayId = nextId('d1');
  const nextIndex = new Set(current.rows.map((row) => row.dayId)).size + current.emptyDays.length + 1;
  current.emptyDays = [
    ...current.emptyDays,
    { dayId, dayIndex: input.index ?? nextIndex, label: input.label, focus: input.focus ?? [] },
  ];
  current.plan = { ...current.plan, daysPerWeek: current.emptyDays.length + new Set(current.rows.map((row) => row.dayId)).size };
  return { overview: overview(), dayId };
}

export function demoPatchProgrammeDay(planId: string, dayId: string, patch: ProgrammeDayPatch): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  current.rows = current.rows.map((row) =>
    row.dayId === dayId
      ? {
          ...row,
          dayLabel: patch.label ?? row.dayLabel,
          dayFocus: patch.focus ?? row.dayFocus,
        }
      : row,
  );
  current.emptyDays = current.emptyDays.map((day) =>
    day.dayId === dayId
      ? { ...day, label: patch.label ?? day.label, focus: patch.focus ?? day.focus }
      : day,
  );
  return overview();
}

export function demoDeleteProgrammeDay(planId: string, dayId: string): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  current.rows = current.rows.filter((row) => row.dayId !== dayId);
  current.emptyDays = current.emptyDays.filter((day) => day.dayId !== dayId);
  const remaining = new Set(current.rows.map((row) => row.dayId)).size + current.emptyDays.length;
  current.plan = { ...current.plan, daysPerWeek: remaining };
  return overview();
}

export function demoCreateProgrammeWarmup(
  planId: string,
  input: { label?: string; focus?: string[] } = {},
): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  if (!current.warmup) {
    current.warmup = {
      dayId: nextId('d0'),
      label: input.label ?? 'Warm-up',
      focus: input.focus ?? [],
      rows: [],
    };
  }
  return overview();
}

export function demoDeleteProgrammeWarmup(planId: string): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  current.warmup = null;
  return overview();
}

/** The rows of a day or of the warm-up session — one shape, two homes. */
function rowsOf(current: DemoStore, dayId: string): ProgrammeRow[] {
  return dayId === current.warmup?.dayId ? current.warmup.rows : current.rows;
}

function setRows(current: DemoStore, dayId: string, rows: ProgrammeRow[]): void {
  if (current.warmup && dayId === current.warmup.dayId) {
    current.warmup = { ...current.warmup, rows };
    return;
  }
  current.rows = rows;
}

export function demoAddProgrammeExercises(
  planId: string,
  dayId: string,
  input: ProgrammeExerciseInput | ProgrammeExerciseInput[],
): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  const warmup = current.warmup && dayId === current.warmup.dayId;
  const rows = rowsOf(current, dayId);
  const dayRow = rows.find((row) => row.dayId === dayId);
  const emptyDay = current.emptyDays.find((day) => day.dayId === dayId);
  const meta = warmup
    ? { dayIndex: 0, label: current.warmup?.label ?? 'Warm-up', focus: current.warmup?.focus ?? [] }
    : dayRow
      ? { dayIndex: dayRow.dayIndex, label: dayRow.dayLabel, focus: dayRow.dayFocus }
      : emptyDay
        ? { dayIndex: emptyDay.dayIndex, label: emptyDay.label, focus: emptyDay.focus }
        : { dayIndex: 1, label: 'Day', focus: [] as string[] };
  const list = Array.isArray(input) ? input : [input];
  const added = list.map((exercise, index) =>
    rowFromInput(dayId, meta, exercise, rows.length + index + 1),
  );
  setRows(current, dayId, [...rows, ...added]);
  if (!warmup) {
    current.emptyDays = current.emptyDays.filter((day) => day.dayId !== dayId);
    current.plan = { ...current.plan, daysPerWeek: new Set(current.rows.map((row) => row.dayId)).size + current.emptyDays.length };
  }
  return overview();
}

export function demoPatchProgrammeExercise(planExerciseId: string, patch: ProgrammeExercisePatch): ProgrammeOverview {
  const current = getStore();
  const apply = (rows: ProgrammeRow[]): ProgrammeRow[] =>
    rows.map((row) => {
      if (row.planExerciseId !== planExerciseId) return row;
      const supersetGroup = patch.supersetGroup === undefined ? row.supersetGroup : patch.supersetGroup;
      const groupType =
        supersetGroup === null
          ? null
          : patch.groupType === undefined
            ? (row.groupType ?? 'superset')
            : patch.groupType;
      return {
        ...row,
        prescribedSets: patch.sets ?? row.prescribedSets,
        repMin: patch.repMin ?? row.repMin,
        repMax: patch.repMax ?? row.repMax,
        targetRpe: patch.targetRpe === undefined ? row.targetRpe : patch.targetRpe,
        targetWeightKg: patch.targetWeightKg === undefined ? row.targetWeightKg : patch.targetWeightKg,
        restSeconds: patch.restSeconds ?? row.restSeconds,
        measure: patch.measure ?? row.measure,
        perSide: patch.perSide ?? row.perSide,
        tempo: patch.tempo === undefined ? row.tempo : patch.tempo,
        supersetGroup,
        groupType,
        notes: patch.notes === undefined ? row.notes : patch.notes,
      };
    });
  current.rows = apply(current.rows);
  if (current.warmup) current.warmup = { ...current.warmup, rows: apply(current.warmup.rows) };
  return overview();
}

/**
 * The in-browser mirror of `PATCH …/days/:dayId/group`: group the named rows,
 * dissolve any cluster left with a single member, and keep the members
 * contiguous — exactly the server's rule, so demo mode cannot lie about it.
 */
export function demoGroupProgrammeExercises(
  planId: string,
  dayId: string,
  input: { planExerciseIds: string[]; group: number | null; groupType?: ExerciseGroupType | null },
): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  const selected = new Set(input.planExerciseIds);
  const groupValue = input.group;
  const typeValue = groupValue === null ? null : (input.groupType ?? 'superset');

  const dayRows = rowsOf(current, dayId);

  let next = dayRows.map((row) =>
    selected.has(row.planExerciseId)
      ? { ...row, supersetGroup: groupValue, groupType: typeValue }
      : row,
  );

  const counts = new Map<number, number>();
  for (const row of next) {
    if (row.supersetGroup === null) continue;
    counts.set(row.supersetGroup, (counts.get(row.supersetGroup) ?? 0) + 1);
  }
  next = next.map((row) =>
    row.supersetGroup !== null && (counts.get(row.supersetGroup) ?? 0) < 2
      ? { ...row, supersetGroup: null, groupType: null }
      : row,
  );

  const chosen = next.filter((row) => selected.has(row.planExerciseId)).map((row) => row.planExerciseId);
  if (chosen.length > 0) {
    const order = next.map((row) => row.planExerciseId);
    const firstIndex = order.findIndex((id) => selected.has(id));
    const before = firstIndex < 0 ? [] : order.slice(0, firstIndex).filter((id) => !selected.has(id));
    const rest = order.filter((id) => !selected.has(id));
    const nextOrder = new Map([...before, ...chosen, ...rest].map((id, index) => [id, index + 1]));
    next = next.map((row) => ({ ...row, orderIndex: nextOrder.get(row.planExerciseId) ?? row.orderIndex }));
  }
  setRows(current, dayId, next);
  return overview();
}

export function demoDeleteProgrammeExercise(planExerciseId: string): ProgrammeOverview {
  const current = getStore();
  current.rows = current.rows.filter((row) => row.planExerciseId !== planExerciseId);
  if (current.warmup) {
    current.warmup = {
      ...current.warmup,
      rows: current.warmup.rows.filter((row) => row.planExerciseId !== planExerciseId),
    };
  }
  return overview();
}

export function demoReorderProgramme(
  planId: string,
  body: { dayIds?: string[]; planExerciseIds?: string[] },
): ProgrammeOverview {
  const current = getStore();
  if (planId !== current.plan.planId) return overview();
  if (body.dayIds?.length) {
    current.rows = current.rows.map((row) => {
      const index = body.dayIds?.indexOf(row.dayId) ?? -1;
      return index >= 0 ? { ...row, dayIndex: index + 1 } : row;
    });
    current.emptyDays = current.emptyDays.map((day) => {
      const index = body.dayIds?.indexOf(day.dayId) ?? -1;
      return index >= 0 ? { ...day, dayIndex: index + 1 } : day;
    });
    current.rows.sort((a, b) => a.dayIndex - b.dayIndex || a.orderIndex - b.orderIndex);
  }
  if (body.planExerciseIds?.length) {
    const order = new Map(body.planExerciseIds.map((id, index) => [id, index + 1]));
    current.rows = current.rows.map((row) => ({
      ...row,
      orderIndex: order.get(row.planExerciseId) ?? row.orderIndex,
    }));
    current.rows.sort((a, b) => a.dayIndex - b.dayIndex || a.orderIndex - b.orderIndex);
  }
  return overview();
}

export function demoActivateProgramme(planId: string): ProgrammeOverview {
  const current = getStore();
  if (!current.hasPlan) return overview();
  if (planId === current.plan.planId) {
    current.plan = { ...current.plan, isActive: true };
    current.history = current.history.map((entry) => ({ ...entry, isActive: false }));
    return overview();
  }
  const entry = current.history.find((candidate) => candidate.planId === planId);
  if (!entry) return overview();
  current.history = current.history.map((candidate) => ({ ...candidate, isActive: candidate.planId === planId }));
  current.plan = { ...current.plan, isActive: false };
  return {
    plan: {
      planId: entry.planId,
      name: entry.name,
      goal: null,
      track: entry.track,
      trackLabel: entry.trackLabel,
      metricsFocus: [],
      weeks: entry.weeks,
      daysPerWeek: entry.daysPerWeek,
      source: 'lifter',
      isActive: true,
      deloadWeek: null,
      weekPlan: [],
    },
    rows: [],
    days: [],
    warmup: null,
  };
}

/* ---------------------------------------------------------------------------
   Live sessions
   ------------------------------------------------------------------------ */

function prescriptionOf(row: ProgrammeRow): Record<string, unknown> {
  return {
    sets: row.prescribedSets,
    repMin: row.repMin,
    repMax: row.repMax,
    targetRpe: row.targetRpe,
    targetWeightKg: row.targetWeightKg,
    restSeconds: row.restSeconds,
    rule: row.progressionRule,
    measure: row.measure,
    perSide: row.perSide,
    supersetGroup: row.supersetGroup,
    groupType: row.groupType,
    tempo: row.tempo,
    targetDistanceM: row.targetDistanceM,
    targetDistanceS: row.targetDurationS,
    isBodyweight: row.isBodyweight,
  };
}

function sessionPayload(): ActiveSessionPayload {
  const current = getStore();
  const session = current.session;
  const exercises = current.exercises.map((exercise) => ({
    ...exercise,
    loggedSets: current.sets.filter((set) => set.sessionExerciseId === exercise.id),
  }));
  return {
    session,
    sessionExercises: exercises,
    totals: session ? totals(exercises) : null,
    serverTime: NOW(),
    softCompletionUntil: null,
  };
}

function totals(exercises: SessionExercise[]): SessionTotalsWire {
  const session = getStore().session;
  let volumeKg = 0;
  let totalReps = 0;
  let setsCompleted = 0;
  let setsPrescribed = 0;
  let prCount = 0;
  let rpeSum = 0;
  let rpeCount = 0;
  for (const exercise of exercises) {
    if (exercise.isWarmup !== true) setsPrescribed += Number(exercise.prescription.sets ?? 0) || 0;
    for (const set of exercise.loggedSets) {
      if (set.isWarmup || set.status === 'pending') continue;
      volumeKg += set.weightKg * set.reps;
      totalReps += set.reps;
      setsCompleted += 1;
      if (set.isPr) prCount += 1;
      if (set.rpe !== null) {
        rpeSum += set.rpe;
        rpeCount += 1;
      }
    }
  }
  return {
    sessionId: session?.id ?? '',
    label: session?.label ?? null,
    startedAt: session?.startedAt ?? NOW(),
    endedAt: session?.endedAt ?? null,
    status: session?.status ?? 'active',
    volumeKg: Math.round(volumeKg * 100) / 100,
    totalReps,
    setsCompleted,
    setsPrescribed,
    prCount,
    failedSets: 0,
    avgRpe: rpeCount === 0 ? null : Math.round((rpeSum / rpeCount) * 10) / 10,
    durationMin: 0,
  };
}

export function demoActiveSessionPayload(): ActiveSessionPayload {
  return sessionPayload();
}

export function demoStartSession(input: StartSessionInput): ActiveSessionPayload {
  const current = getStore();
  if (current.session) return sessionPayload();
  const dayRows = input.planDayId ? current.rows.filter((row) => row.dayId === input.planDayId) : [];
  const dayLabel = dayRows[0]?.dayLabel ?? null;
  current.session = {
    id: uuid('5105', current.seq + 200),
    planDayId: input.planDayId ?? null,
    label: input.label ?? dayLabel ?? 'Ad-hoc session',
    startedAt: NOW(),
    endedAt: null,
    status: 'active',
    perceivedExertion: null,
    moodScore: null,
    track: current.plan.track,
  };
  // The block's warm-up runs before the day's first movement (0019).
  const warmupRows = input.planDayId && current.warmup ? current.warmup.rows : [];
  const warmup = warmupRows.map((row, index) => ({
    id: uuid('5e0', index + 1 + current.seq),
    sessionId: current.session?.id ?? '',
    exerciseId: row.exerciseId,
    orderIndex: index + 1,
    exerciseName: row.exerciseName,
    prescription: prescriptionOf(row),
    notes: row.notes,
    isWarmup: true,
    loggedSets: [],
  }));
  const working = dayRows.map((row, index) => ({
    id: uuid('5e1', index + 1 + current.seq),
    sessionId: current.session?.id ?? '',
    exerciseId: row.exerciseId,
    orderIndex: warmup.length + index + 1,
    exerciseName: row.exerciseName,
    prescription: prescriptionOf(row),
    notes: row.notes,
    isWarmup: false,
    loggedSets: [],
  }));
  current.exercises = [...warmup, ...working];
  current.sets = [];
  return sessionPayload();
}

export function demoAddSessionExercise(sessionId: string, input: AddSessionExerciseInput): ActiveSessionPayload {
  const current = getStore();
  if (!current.session || current.session.id !== sessionId) return sessionPayload();
  if (current.exercises.some((exercise) => exercise.exerciseId === input.exerciseId)) return sessionPayload();
  current.exercises = [
    ...current.exercises,
    {
      id: nextId('5e1'),
      sessionId,
      exerciseId: input.exerciseId,
      orderIndex: input.orderIndex ?? current.exercises.length + 1,
      exerciseName: input.exerciseId,
      prescription: { sets: 3, repMin: 8, repMax: 12, targetRpe: 7, targetWeightKg: null, restSeconds: 90, measure: 'reps', perSide: false },
      notes: null,
      isWarmup: false,
      loggedSets: [],
    },
  ];
  return sessionPayload();
}

function previousBest(exerciseId: string, exceptSetId: string | null): number | null {
  const current = getStore();
  let best: number | null = null;
  for (const set of current.sets) {
    if (set.id === exceptSetId) continue;
    if (set.exerciseId !== exerciseId || set.reps <= 0) continue;
    const e1rm = set.weightKg * (1 + Math.min(set.reps, 12) / 30);
    if (best === null || e1rm > best) best = e1rm;
  }
  return best;
}

function setResponse(set: SessionSet): SessionSetResponse {
  const current = getStore();
  const exercises = current.exercises.map((exercise) => ({
    ...exercise,
    loggedSets: current.sets.filter((candidate) => candidate.sessionExerciseId === exercise.id),
  }));
  const personalRecord: PersonalRecordWire | null = set.isPr
    ? {
        id: `pr-${set.id}`,
        exerciseId: set.exerciseId,
        exerciseName: current.exercises.find((exercise) => exercise.id === set.sessionExerciseId)?.exerciseName ?? null,
        weightKg: set.weightKg,
        reps: set.reps,
        estimated1rmKg: Math.round(set.weightKg * (1 + Math.min(set.reps, 12) / 30) * 100) / 100,
        previousEstimated1rmKg: null,
        achievedAt: NOW(),
      }
    : null;
  return {
    set: { ...set },
    personalRecord,
    sessionTotals: totals(exercises),
    coachHint: null,
    idempotentReplay: false,
    slotConflict: false,
  };
}

export function demoLogSessionSet(sessionId: string, input: LogSessionSetInput): SessionSetResponse {
  const current = getStore();
  const exercise = current.exercises.find((candidate) => candidate.id === input.sessionExerciseId);
  const replay = input.clientSetId ? current.sets.find((set) => set.clientSetId === input.clientSetId) : undefined;
  if (replay) return setResponse(replay);
  const id = nextId('5e7');
  const prior = previousBest(exercise?.exerciseId ?? '', null);
  const e1rm = input.reps > 0 ? input.weightKg * (1 + Math.min(input.reps, 12) / 30) : 0;
  const isWarmup = input.isWarmup === true || exercise?.isWarmup === true;
  const isPr = !isWarmup && input.reps > 0 && input.weightKg > 0 && (prior === null || e1rm > prior);
  const set: SessionSet = {
    id,
    sessionExerciseId: input.sessionExerciseId,
    exerciseId: exercise?.exerciseId ?? '',
    setIndex: input.setIndex,
    weightKg: input.weightKg,
    reps: input.reps,
    rpe: input.rpe ?? null,
    status: isPr ? 'pr' : (input.status ?? 'done'),
    isPr,
    isWarmup,
    measure: (input.measure ?? 'reps') as SetMeasure,
    distanceM: input.distanceM ?? null,
    durationS: input.durationS ?? null,
    perSide: input.perSide === true,
    tempo: null,
    painScore: input.painScore ?? null,
    performedAt: NOW(),
    clientSetId: input.clientSetId ?? null,
  };
  current.sets = [...current.sets.filter((candidate) => !(candidate.sessionExerciseId === input.sessionExerciseId && candidate.setIndex === input.setIndex)), set];
  void sessionId;
  return setResponse(set);
}

export function demoPatchSessionSet(setId: string, patch: Partial<LogSessionSetInput>): SessionSetResponse {
  const current = getStore();
  let updated: SessionSet | null = null;
  current.sets = current.sets.map((set) => {
    if (set.id !== setId) return set;
    const merged: SessionSet = {
      ...set,
      weightKg: patch.weightKg ?? set.weightKg,
      reps: patch.reps ?? set.reps,
      rpe: patch.rpe === undefined ? set.rpe : patch.rpe,
      status: patch.status ?? set.status,
      painScore: patch.painScore === undefined ? set.painScore : patch.painScore,
    };
    const prior = previousBest(merged.exerciseId, merged.id);
    const e1rm = merged.reps > 0 ? merged.weightKg * (1 + Math.min(merged.reps, 12) / 30) : 0;
    const isPr = !merged.isWarmup && merged.reps > 0 && merged.weightKg > 0 && (prior === null || e1rm > prior);
    updated = { ...merged, isPr, status: isPr ? 'pr' : merged.status === 'pr' ? 'done' : merged.status };
    return updated;
  });
  if (!updated) throw new Error('Set not found.');
  return setResponse(updated);
}

export function demoDeleteSessionSet(setId: string): boolean {
  const current = getStore();
  const before = current.sets.length;
  current.sets = current.sets.filter((set) => set.id !== setId);
  return current.sets.length < before;
}

export function demoCompleteSession(sessionId: string, input: { perceivedExertion?: number | null; moodScore?: number | null }): CompleteSessionResponse {
  const current = getStore();
  if (current.session && current.session.id === sessionId) {
    current.session = {
      ...current.session,
      status: 'completed',
      endedAt: NOW(),
      perceivedExertion: input.perceivedExertion ?? null,
      moodScore: input.moodScore ?? null,
    };
  }
  const exercises = current.exercises.map((exercise) => ({
    ...exercise,
    loggedSets: current.sets.filter((set) => set.sessionExerciseId === exercise.id),
  }));
  return {
    session: current.session ?? {
      id: sessionId,
      planDayId: null,
      label: null,
      startedAt: NOW(),
      endedAt: NOW(),
      status: 'completed',
      perceivedExertion: null,
      moodScore: null,
      track: 'strength',
    },
    totals: totals(exercises),
    coachRunInvalidated: false,
  };
}

export function demoEndSession(): void {
  const current = getStore();
  current.session = null;
  current.exercises = [];
  current.sets = [];
}
